/**
 * Lee la respuesta de Business Central al crear la cabecera de una factura.
 *
 * `Importaciones_insertaFacturasVenta` es una acción no vinculada, y esas
 * devuelven **un solo escalar**: `value`. Cuando hizo falta devolver dos datos
 * --el número asignado y el `System Id`, que es lo que permite contabilizar
 * acto seguido-- se serializó un JSON dentro de ese escalar.
 *
 * ## Por qué esto no puede depender del país
 *
 * Antes el terminal desempaquetaba ese JSON **sólo si el país era Paraguay u
 * Honduras**, que fueron los primeros en devolverlo. El día que la acción de
 * otro país empezó a contestar igual, el terminal se quedó con el JSON entero
 * y lo mandó como `Document_No_` de cada línea -- un campo de 20 caracteres
 * que sólo sirve para relacionar la línea con su factura. La inserción de
 * líneas falló del lado de Business Central, con el texto desbordado, y sin
 * ninguna pista de dónde venía. Le pasó a **Perú** el 28 de agosto de 2026.
 *
 * Por eso ya no se pregunta el país: **se mira lo que llegó**. Un texto que
 * empieza por llave se interpreta como JSON; cualquier otra cosa es el número
 * tal cual, que es como contestaban todos los ambientes antes.
 *
 * ## El apaño que había, y por qué era un riesgo
 *
 * El AL antiguo devolvía ese JSON **sin la coma entre los dos campos**:
 *
 *     {"No.":"000033""System Id":"{2f0e...}"}
 *
 * y el terminal lo reparaba a ciegas con `replace(/""/g, '","')` antes de
 * interpretarlo. La reparación funcionaba mientras esa fuera la única forma de
 * encontrar dos comillas seguidas.
 *
 * Ya no lo es: el AL actual pone la coma, así que hoy la única manera de que
 * aparezca `""` es que **un valor venga vacío** — y entonces el apaño convierte
 * `{"No.":"","System Id":"x"}` en `{"No.":","," ,"System Id":"x"}`, que no es
 * JSON, y la factura se cae con un error de sintaxis en vez de decir que BC no
 * devolvió número.
 *
 * Ahora se interpreta tal cual viene, y la reparación sólo entra si eso falla:
 * el texto bien formado nunca se toca, y el mal formado se sigue entendiendo
 * mientras algún ambiente conserve el AL viejo.
 */

/** Lo que Business Central acepta como número de documento: 20 caracteres. */
export const LARGO_MAXIMO_DEL_NUMERO = 20;

/** Quita puntos, espacios y llaves del nombre de un campo. */
const limpiarClave = (clave) => String(clave).replace(/[.\s{}]/g, "").trim();

/** El `System Id` viene envuelto en llaves; el número, no. */
const limpiarValor = (valor) => (typeof valor === "string" ? valor.replace(/[{}]/g, "") : valor);

/**
 * @param {string} rawValue El texto que devolvió Business Central.
 * @returns {{ No: string|undefined, SystemId: string|undefined }}
 * @throws {SyntaxError} Si empieza por llave y no se puede interpretar ni tal
 *   cual ni reparado.
 */
export const leerCabeceraDeBC = (rawValue) => {
  const texto = String(rawValue ?? "").trim();

  // La respuesta de siempre: el número pelado. No hay nada que desempaquetar.
  if (!texto.startsWith("{")) return { No: texto, SystemId: "" };

  let parsed;
  try {
    parsed = JSON.parse(texto);
  } catch {
    // Sólo acá: el AL viejo, que se comía la coma entre los dos campos.
    parsed = JSON.parse(texto.replace(/""/g, '","'));
  }

  const normalizado = {};
  for (const [clave, valor] of Object.entries(parsed ?? {})) {
    normalizado[limpiarClave(clave)] = limpiarValor(valor);
  }

  return { No: normalizado.No, SystemId: normalizado.SystemId };
};

/**
 * Si el número que devolvió la cabecera se puede usar para relacionar las
 * líneas y los pagos con su factura.
 *
 * Es el guardia que faltaba: cortar acá deja un error legible en el terminal,
 * en vez de mandarle a Business Central un texto que no cabe en el campo y que
 * del otro lado no dice de dónde salió.
 */
export const esNumeroDeDocumentoUsable = (no) => {
  const texto = String(no ?? "").trim();

  if (!texto) return false;
  if (texto.length > LARGO_MAXIMO_DEL_NUMERO) return false;
  // Sólo asteriscos tampoco sirve, y la razón es que Business Central aplica la
  // misma regla desde la 1.0.4.109. Si las dos no coincidieran, BC podría devolver
  // por idempotencia un borrador que aquí se rechaza, el terminal volvería a pedir
  // la cabecera y BC devolvería el mismo: un bucle.
  //
  // No es el caso que se ve en la práctica: el `***` del importador vive en un
  // registro temporal y no llega a guardarse. Lo que sí queda en BC cuando la
  // serie no da número es el número **vacío**, que ya se rechaza arriba. Acuerdo
  // `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H4.
  if (/^\*+$/.test(texto)) return false;

  return !/[{}"]/.test(texto);
};
